import { useEffect, useMemo, useState } from "react";
import Select from "react-select";

import { useDispatch, useSelector } from "react-redux";
import {
  handleStage,
  resetFormData,
  setFormData,
  handleJoyRideRunning,
  setField,
} from "./redux/slice";

import {
  GetBoardsThunk,
  GetClasesThunk,
  GetClasssesNewThunk,
  GetClassSubjectChaptersThunk,
  GetMediumThunk,
  GetPaperMetaDataThunk,
  GetQuestionCategoriesThunk,
  GetQuestionsThunk,
  GetSubjectsNewThunk,
  GetSubjectsThunk,
} from "./redux/thunk";

import "../css/questionPaper.css";
import RightPanel from "./components/rightPanel";
import LeftPanel from "./components/leftPanel";
import { resetData, updateCategories } from "./redux/editorSlice";
import { findById, getHeaderData } from "./components/others";
import ErrorAlert from "./components/ErrorAlert";
import Confirmation from "./components/confirmation";
import { getMasterDataByUserId } from "../../../pages/masterData/redux/masterDataThunk";
import { useLocation } from "react-router-dom";
import SkeletonLoader from "./components/loader/skeletonLoader";
import toast from "react-hot-toast";
import ToastComponent from "../../../components/Other/Toast";
// import { JoyTour } from "../../../components/TourGuide";

import Joyride from "react-joyride";
import { getValidationMessages } from "./redux/selectors";
import {
  getDemoTourSteps,
  getUserTourSteps,
} from "../../../tour/QuestionPaperSteps";
import useSpeech from "../../../hooks/useSpeech";
import { setAppdAiUser } from "../../../utils/appdAiHelpers";

const typeOptions = [
  { value: "Question paper", label: "Question paper" },
  { value: "Worksheet", label: "Worksheet" },
];
// Mobile-only responsive styles
const mobileStyles = `
@media (max-width: 768px) {
  /* Add this rule for mobile */
  .container-style-mobile {
    max-width: 100% !important;
    padding: 10px 15px !important;
  }
  
  /* 1) Type of Paper - FULL WIDTH on mobile */
  .mobile-type-paper-row {
    display: flex !important;
    flex-direction: column !important;
  }
  .mobile-type-paper-row > div {
    width: 100% !important;
  }
  
  /* 2) Maximum Marks + Duration - SIDE BY SIDE on mobile */
  .mobile-marks-duration-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 8px !important;
  }
  .mobile-marks-duration-row > div {
    width: 50% !important;
    flex: 1 !important;
    min-width: 0 !important;
  }
  
  /* 3) Examination Name - FULL WIDTH on mobile */
  .mobile-exam-name-row {
    display: block !important;
  }
  .mobile-exam-name-row > div {
    width: 100% !important;
  }
  
  /* 4) Class and Subject - SIDE BY SIDE on mobile */
  .mobile-class-subject-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    gap: 8px !important;
  }
  .mobile-class-subject-row > div {
    width: 50% !important;
    flex: 1 !important;
    min-width: 0 !important;
  }
  
  /* 5) Fix select boxes - Keep dropdown arrow INSIDE the field */
  .custom-select-wrapper {
    width: 100% !important;
  }
  
  .custom-select__control {
    min-width: 0 !important;
    width: 100% !important;
  }
  
  .custom-select__value-container {
    overflow: visible !important;
    flex-wrap: nowrap !important;
    min-width: 0 !important;
    padding: 0 8px !important;
    position: relative !important;
  }

  .custom-select__indicators {
    flex-shrink: 0 !important;
  }

  .custom-select__indicator-separator {
    display: none !important;
  }

  .custom-select__dropdown-indicator {
    padding: 4px !important;
  }

  /* 6) Fix text overflow in dropdowns */
  .custom-select__single-value {
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: calc(100% - 8px) !important;
  }
  .custom-select__placeholder {
    color: #808080 !important;
    opacity: 1 !important;
    visibility: visible !important;
    display: block !important;
    position: static !important;
    transform: none !important;
    margin: 0 !important;
    white-space: nowrap !important;
  }
  
  /* 7) Fix input fields to not overflow */
  .custom-input {
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* 8) Ensure select container doesn't overflow */
  .mobile-select-fix {
    overflow: visible !important;
  }
  
  .mobile-select-fix .custom-select-wrapper,
  .mobile-select-fix .joyride-select-wrapper {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Joyride tooltip mobile fixes */
  .__floater {
    max-width: 270px !important;
  }

  .__floater__body {
    max-width: 270px !important;
  }

  .react-joyride__tooltip {
    max-width: 260px !important;
    width: auto !important;
    padding: 4px !important;
    font-size: 13px !important;
  }
}
`;
const formatTwoDigits = (num) => String(num).padStart(2, "0");

const getDurationText = (a, b) => {
  const parts = [];

  if (a > 0) {
    parts.push(`${formatTwoDigits(a)} ${a === 1 ? "hour" : "hours"}`);
  }

  if (b > 0) {
    parts.push(`${formatTwoDigits(b)} ${b === 1 ? "minute" : "minutes"}`);
  }

  return parts.length ? parts.join(" ") : "No duration";
};

function CreateQuestionPaper() {
  const dispatch = useDispatch();
  const location = useLocation();
  const demoTourSteps = useMemo(() => getDemoTourSteps(), []);
  const userTourSteps = useMemo(() => getUserTourSteps(), []);
  const { user } = useSelector((store) => store.authSlice);

  const activeSteps = user.admin_id === 104 ? demoTourSteps : userTourSteps;
  const isWalkthroughCompleted =
    localStorage.getItem("questionPaperTourCompleted") === "true";

  // const walkthroughStatusRaw = localStorage.getItem("walkthroughStatus");

  // const walkthroughStatus = walkthroughStatusRaw
  //   ? JSON.parse(walkthroughStatusRaw)
  //   : [];

  // const moduleWalkthrough = walkthroughStatus.find(
  //   (module) => module.module_id === 2
  // );

  // const isWalkthroughCompleted =
  //   moduleWalkthrough?.is_completed_walkthrough === 1;

  const [currentStep, setCurrentStep] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const {
    stage,
    formData,
    classes,
    subjects,
    loader,
    error,
    boardsList,
    mediumList,
    classesNew,
    subjectsNew,
    isJoyRideRunning,
    hours,
    minutes,
  } = useSelector((store) => store.createQstnPaper);
  const {
    loader: createPaperEditorLoader,
    categories,
    questions,
  } = useSelector((store) => store.createPaperEditor);
  const { displayMessage, message: validationMessage } = useSelector(
    getValidationMessages,
  );

  // Use the global speech hook
  const {
    speak: speakText,
    isSpeaking,
    voicesReady,
    speechEnabled,
    setSpeechEnabled,
  } = useSpeech();

  useEffect(() => {
    if (!isWalkthroughCompleted) {
      dispatch(handleJoyRideRunning(true));
    }
  }, [dispatch, isWalkthroughCompleted]);

  useEffect(() => {
    // if (user) setAppdAiUser(user, "Create Question Paper");
    if (user) setAppdAiUser(user, "demo");
  }, []);

  useEffect(() => {
    document.body.classList.add("question-paper-screen");

    return () => {
      document.body.classList.remove("question-paper-screen");
    };
  }, []);


  const boardOptions =
    boardsList?.data
      ?.filter((e) => e?.status === 1)
      ?.map((e) => ({
        value: e.id,
        label: e.name,
        // label: `${e.name} (${e.description})`,
      })) || [];

  const classesOptions =
    classesNew?.map((e) => ({
      value: e.class_id,
      label: e.class_name,
    })) || [];

  const subjectOptions =
    subjectsNew?.map((e) => ({
      value: e.id,
      label: e.subject_name,
    })) || [];

  // 🔹 Helper function to check if max marks are reached
  const isMaxMarksReached = () => {
    const maxMarks = Number(formData.maxMarks);
    const filteredCategories = categories.filter(
      (c) => c.type_id !== "header" && c.display === true,
    );

    const totalPaperMarks = filteredCategories.reduce((acc, curCat) => {
      return acc + Number(curCat.marks || 0) * Number(curCat.questions || 1);
    }, 0);

    return totalPaperMarks >= maxMarks;
  };

  const goToStepByTarget = (target, beforeAction) => {
    if (beforeAction) beforeAction();

    requestAnimationFrame(() => {
      setTimeout(() => {
        const steps = user.admin_id === 104 ? demoTourSteps : userTourSteps;
        const index = steps.findIndex((step) => step.target === target);

        if (index !== -1) {
          setCurrentStep(index);
        }
      }, 300); // 👈 DOM settle delay
    });
  };

  const isNextDisabled = (stepIndex) => {
    const steps = user.admin_id === 104 ? demoTourSteps : userTourSteps;
    const target = steps[stepIndex]?.target;

    switch (target) {
      case ".type-of-paper-step":
        return !formData.type;

      case ".maximun-marks-step":
        return !formData.maxMarks || Number(formData.maxMarks) <= 0;

      case ".duration-step":
        return !formData.duration || Number(formData.duration) <= 0;

      case ".exam-name-step":
        return !formData.examName?.trim();

      case ".class-step":
        return !formData.class;

      case ".subject-step":
        return !formData.subject;

      case ".terms-and-conditions-step":
        // optional field → allow next
        return false;

      default:
        return false;
    }
  };
  useEffect(() => {
    console.log("User language:", navigator.language);
  }, []);
  useEffect(() => {
    dispatch(GetBoardsThunk());
  }, []);

  // const handleMediumList = async (board_id) => {
  //   try {
  //     const response = await dispatch(
  //       GetMediumThunk({
  //         board_id,
  //       }),
  //     );
  //     if (response?.payload?.data) {
  //       dispatch(
  //         setField({
  //           key: "mediumList",
  //           value: response?.payload?.data || [],
  //         }),
  //       );
  //     }
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  const mediumOptions =
    // label: `${e.name} (${e.description})`,
    mediumList?.length > 0
      ? mediumList.map((e) => ({
        value: e.id,
        label: e.name,
      }))
      : [];

  const CustomTooltip = ({
    index,
    step,
    primaryProps,
    closeProps,
    tooltipProps,
  }) => {
    return (
      <div
        {...tooltipProps}
        style={{
          background: "#fff",
          padding: window.innerWidth <= 768 ? "8px 10px" : "12px",
          borderRadius: "8px",
          maxWidth: window.innerWidth <= 768 ? "260px" : "380px",
          boxSizing: "border-box",
          fontSize: window.innerWidth <= 768 ? "13px" : "inherit",
        }}
      >
        {/* Content */}
        <div
          style={{ marginBottom: window.innerWidth <= 768 ? "6px" : "12px" }}
        >
          {step.content}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: window.innerWidth <= 768 ? "8px" : "12px",
          }}
        >
          {/* Custom Skip */}
          {step.showSkip && (
            <button
              onClick={(e) => {
                e.preventDefault();
                primaryProps.onClick(e); // ✅ pass event
              }}
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                cursor: "pointer",
                fontSize: window.innerWidth <= 768 ? "11px" : "14px",
                padding: window.innerWidth <= 768 ? "2px 6px" : undefined,
              }}
            >
              Skip
            </button>
          )}

          {/* Next / Finish Button */}
          {step.isFinalStep ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                primaryProps.onClick(e);
                setCurrentStep(0);
                window.speechSynthesis.cancel();
                dispatch(handleJoyRideRunning(false));
                window.location.href = "/dashboard";
              }}
              style={{
                background: "#6c4cff",
                color: "#fff",
                border: "none",
                padding: window.innerWidth <= 768 ? "2px 8px" : "6px 14px",
                borderRadius: window.innerWidth <= 768 ? "4px" : "6px",
                cursor: "pointer",
                fontSize: window.innerWidth <= 768 ? "11px" : "inherit",
              }}
            >
              Finish
            </button>
          ) : (
            step.showNext && (
              <button
                {...primaryProps}
                disabled={isNextDisabled(index)}
                style={{
                  ...primaryProps.style,
                  background: isNextDisabled(index) ? "#ccc" : "#6c4cff",
                  color: "#fff",
                  border: "none",
                  padding: window.innerWidth <= 768 ? "1px 8px" : "6px 14px",
                  borderRadius: window.innerWidth <= 768 ? "4px" : "6px",
                  cursor: isNextDisabled(index) ? "not-allowed" : "pointer",
                  pointerEvents: isNextDisabled(index) ? "none" : "auto",
                  fontSize: window.innerWidth <= 768 ? "11px" : "inherit",
                  lineHeight: window.innerWidth <= 768 ? "1" : "inherit",
                  height: window.innerWidth <= 768 ? "22px" : "auto",
                  minHeight: window.innerWidth <= 768 ? "0" : undefined,
                }}
              >
                Next
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  // Handle escape key and body scroll lock for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowPreviewModal(false);
      }
    };

    if (showPreviewModal) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [showPreviewModal]);

  useEffect(
    function () {
      if (location.state?.paperId && location.state?.type) {
        dispatch(
          GetPaperMetaDataThunk({
            paperId: location.state.paperId,
            type: location.state.type,
          }),
        );

        if (location.state.type === 1) {
          dispatch(handleStage(3));
        }
      } else {
        dispatch(GetClasesThunk());
      }

      dispatch(getMasterDataByUserId(user.id));

      return () => {
        dispatch(handleStage(1));
        dispatch(resetFormData());
        dispatch(resetData());
        toast.dismiss();
        setCurrentStep(0);
        setShowPreviewModal(false);
      };
    },

    [dispatch, location, user],
  );

  const handleOnChange = async (e) => {
    const name = e.target.name;
    const value = e.target?.value || "";

    dispatch(setFormData({ name, value }));

    if (name === "type") {
      ToastComponent(user, "Please Enter Maximum Marks..", 1);
      setCurrentStep((prev) => prev + 1);
      // JoyTour("create-paper-max-marks", "Please Enter Maximum Marks..");
    }

    if (name === "class" && value) {
      // await dispatch(GetSubjectsThunk({ classId: value }));
      await dispatch(GetSubjectsThunk({ classId: value }));

      ToastComponent(user, "Please Select a Subject..", 1);
      setCurrentStep((prev) => prev + 1);
      // JoyTour("create-paper-subject", "Please Select a Subject..");
    }

    if (name === "subject" && value) {
      await dispatch(
        GetClassSubjectChaptersThunk({
          subjectId: value,
          classId: formData.class,
        }),
      );

      ToastComponent(user, "Please Click Select questions button..", 1);
      setCurrentStep((prev) => prev + 1);
      // JoyTour(
      //   "create-paper-select-qstns-btn",
      //   "Please Click Select questions button.."
      // );
    }

    if (name === "chapter" && value) {
      dispatch(
        GetQuestionsThunk({
          subjectId: formData.subject,
          classId: formData.class,
          chapterId: value,
        }),
      );
    }

    if (
      name === "schoolName" ||
      name === "examName" ||
      name === "maxMarks" ||
      name === "termsText" ||
      name === "displayTerms" ||
      name === "displayHeader"
    ) {
      const { subjectName, className } = findById(formData, subjects, classes);
      const updatedFormData = {
        ...formData,
        subjectName,
        className,
        [name]: value,
      };
      dispatch(updateCategories(getHeaderData(updatedFormData).categoryData));
    }
  };

  const getStepIndex = (steps, target) =>
    steps.findIndex((step) => step.target === target);

  console.log(currentStep, "currentStep FORM PAPER");

  // State → Output
  // useEffect(() => {
  //   const durationText = getDurationText(hours, minutes);
  //   handleOnChange({ target: { name: "duration", value: durationText } });
  // }, [hours, minutes]);
  useEffect(() => {
    // if (hours || minutes) {
    const durationText = getDurationText(hours, minutes);
    handleOnChange({ target: { name: "duration", value: durationText } });
    // }
  }, [hours, minutes]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // if (!hours === "00" && minutes === "00") {

    if (!formData.maxMarks || Number(formData.maxMarks) <= 0) {
      alert("Please enter the maximum marks");
      return;
    }

    if (Number(hours) === 0 && Number(minutes) === 0) {
      alert("Please enter the duration of the exam");
      return;
    }

    // console.log("durationText", durationText);
    // alert("durationText", durationText);
    // handleOnChange({ target: { name: "duration", value: durationText } });
    // dispatch(
    //   setFormData({
    //     key: "duration",
    //     value: durationText,
    //   }),
    // );
    dispatch(handleStage(2));
    const { subjectName, className } = findById(formData, subjects, classes);
    const updatedFormData = { ...formData, subjectName, className };
    // alert("hiiii");
    dispatch(
      GetQuestionCategoriesThunk({
        updateHeader: true,
        formData: updatedFormData,
      }),
    );

    // ✅ DIFFERENT TOUR FLOW BASED ON USER
    setTimeout(() => {
      if (user.admin_id === 104) {
        // DEMO USER → True/False step
        setCurrentStep(getStepIndex(demoTourSteps, ".true-false-step-3"));
      } else {
        // NORMAL USER → Category selection step
        setCurrentStep(getStepIndex(userTourSteps, ".category-select-step"));
      }
    }, 300); // ensures stage-2 UI is rendered
  };

  // Preview Modal Component
  const PreviewModal = () => (
    <div
      className={`preview-modal-overlay ${showPreviewModal ? "show" : ""}`}
      onClick={(e) => {
        if (e.target.classList.contains("preview-modal-overlay")) {
          setShowPreviewModal(false);
        }
      }}
    >
      <div className="preview-modal-content">
        <div className="preview-modal-header">
          <h3>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginRight: "8px" }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Question Paper Preview
          </h3>
          <button
            className="preview-modal-close"
            onClick={() => setShowPreviewModal(false)}
            aria-label="Close preview"
          >
            ×
          </button>
        </div>
        <div className="preview-modal-body">
          <div className="right-panel-content">
            <RightPanel />
          </div>
        </div>
      </div>
    </div>
  );

  if (loader) return <SkeletonLoader type={1} />;

  const containerStyle = {
    maxWidth: "900px",
    margin: "15px auto",
    padding: "15px 30px",
    backgroundColor: "rgb(123 47 247 / 51%)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    borderRadius: "12px",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
  };

  const titleStyle = {
    fontSize: "22px",
    fontWeight: "bold",
    marginBottom: "20px",
  };

  const closeButtonStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 10001,
    backgroundColor: "#ff4757",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "44px",
    height: "44px",
    fontSize: "28px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(255, 71, 87, 0.4)",
    transition: "all 0.3s ease",
  };

  console.log(currentStep, "currentStep FORM PAPER");
  // const getDurationText = (a, b) => {
  //   const hourLabel = a < 2 ? "hour" : "hours";
  //   const minuteLabel = b < 2 ? "minute" : "minutes";

  //   return `${formatTwoDigits(a)} ${hourLabel} ${formatTwoDigits(
  //     b,
  //   )} ${minuteLabel}`;
  // };

  return (
    <>
      {/* Mobile-only styles - injected via style tag */}
      <style>{mobileStyles}</style>
      {/* <Joyride
        run={isJoyRideRunning && !location.state?.paperId && !isWalkthroughCompleted}
        steps={user.admin_id === 104 ? demoTourSteps : userTourSteps}
        stepIndex={currentStep}
        continuous={true}
        showProgress={false}
        spotlightClicks={true}
        disableOverlay={false}
        disableOverlayClose={true}
        tooltipComponent={CustomTooltip}
        hideCloseButton={true}
        showSkipButton={false}
        scrollOffset={100}
        floaterProps={{
          disableAnimation: true,
        }}
        styles={{
          options: { zIndex: 10000 },
          buttonBack: { display: "none" },
          overlay: {
            zIndex: 8000,
          },
          buttonNext: {
            backgroundColor: displayMessage ? "#ccc" : "#6c4cff",
            cursor: displayMessage ? "not-allowed" : "pointer",
            pointerEvents: displayMessage ? "none" : "auto",
            display: activeSteps[currentStep]?.showNext ? "block" : "none",
          },
          buttonSkip: {
            marginTop: "0px",      // 👈 YOUR REQUIREMENT
            color: "#6b7280",
            fontSize: "14px",
          },
          tooltip: {
            width: "auto",
            maxWidth: window.innerWidth <= 768 ? "260px" : "380px",
            minHeight: window.innerWidth <= 768 ? "auto" : "50px",
            padding: window.innerWidth <= 768 ? "4px" : "5px",
            borderRadius: "8px",
          },

          tooltipContent: {
            padding: window.innerWidth <= 768 ? "4px 6px" : "6px 8px",
            fontSize: window.innerWidth <= 768 ? "13px" : "16px",
            lineHeight: window.innerWidth <= 768 ? "1.3" : "1.4",
          },

        }}

 callback={(data) => {
  const { action, index, type, step } = data;

  // 🔊 FAST speak (no big delay)
  if ((type === "tour:start" || type === "step:before") && step?.content) {
    requestAnimationFrame(() => {
      speakText(step.content);
    });
  }

  if (type === "tooltip:render") return;

  if (type === "step:after" && action === "next") {
    setCurrentStep(index + 1);
  }
}}


      /> */}

      {/* 🔹 Joyride Close Button - Top Right Corner */}
      {/* {isJoyRideRunning && !isWalkthroughCompleted && (
        <button
          style={closeButtonStyle}
          onClick={() => {
              window.speechSynthesis.cancel();
            dispatch(handleJoyRideRunning(false));
            setCurrentStep(0);
          }}
          title="Close tour"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#ff3838";
            e.target.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#ff4757";
            e.target.style.transform = "scale(1)";
          }}
        >
          ✕
        </button>
      )} */}

      {error !== null && <ErrorAlert message={error} />}
      {stage === 1 && (
        <div className="container_bg">
          {/* <div>
            <h3></h3>
            <small>Configure your question paper settings</small>
          </div> */}
          <div className="question-paper-container" style={containerStyle}>
            <h2 className="question-paper-title" style={titleStyle}>
              Create Question Paper
            </h2>
            <form onSubmit={handleFormSubmit}>
              <fieldset disabled={loader}>
                {/* Row 1: Type of Paper - FULL WIDTH on mobile, w-50 on desktop */}
                <div className="d-flex gap-2 mb-3 school-crt-form mobile-type-paper-row">
                  <div className="w-50 type-of-paper-step mobile-select-fix">
                    <label>Type of Paper</label>
                    <div className="joyride-select-wrapper">
                      <Select
                        options={typeOptions}
                        required
                        isSearchable={false}
                        value={
                          typeOptions.find(
                            (opt) => opt.value === formData.type,
                          ) || null
                        }
                        onChange={(e) => {
                          const name = "type";
                          const value = e.value;
                          handleOnChange({ target: { name, value } });
                        }}
                        placeholder="Select Type"
                        className="custom-select-wrapper custom-select create-paper-class"
                        classNamePrefix="custom-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                          control: (base) => ({
                            ...base,
                            minHeight: "38px",
                            height: "38px",
                            minWidth: 0,
                            width: "100%",
                            flexWrap: "nowrap",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: "36px",
                            padding: "0 8px",
                            flexWrap: "nowrap",
                            overflow: "hidden",
                            minWidth: 0,
                            flex: 1,
                          }),
                          singleValue: (base) => ({
                            ...base,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            position: "absolute",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "calc(100% - 8px)",
                            color: "#808080",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: "36px",
                            flexShrink: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: "none",
                          }),
                          dropdownIndicator: (base) => ({
                            ...base,
                            padding: "4px",
                          }),
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-50 type-of-paper-step mobile-select-fix">
                    <label>Boards</label>
                    <div className="joyride-select-wrapper">
                      <Select
                        options={boardOptions}
                        required
                        isSearchable={false}
                        value={
                          boardOptions.find(
                            (opt) => opt.value === formData.board_id,
                          ) || null
                        }
                        onChange={async (e) => {
                          // const name = "type";
                          const value = e.value;
                          dispatch(setFormData({ name: "board_id", value }));
                          dispatch(GetMediumThunk({ board_id: value }));
                        }}
                        placeholder="Select Board"
                        className="custom-select-wrapper custom-select create-paper-class"
                        classNamePrefix="custom-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                          control: (base) => ({
                            ...base,
                            minHeight: "38px",
                            height: "38px",
                            minWidth: 0,
                            width: "100%",
                            flexWrap: "nowrap",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: "36px",
                            padding: "0 8px",
                            flexWrap: "nowrap",
                            overflow: "hidden",
                            minWidth: 0,
                            flex: 1,
                          }),
                          singleValue: (base) => ({
                            ...base,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            position: "absolute",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "calc(100% - 8px)",
                            color: "#808080",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: "36px",
                            flexShrink: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: "none",
                          }),
                          dropdownIndicator: (base) => ({
                            ...base,
                            padding: "4px",
                          }),
                        }}
                      />
                    </div>
                  </div>
                  {/* Maximum Marks - Hidden on mobile in this row, shown in next row */}
                </div>
                <div className="d-flex gap-2 mb-3 school-crt-form mobile-marks-duration-row">
                  <div className="w-50 maximun-marks-step d-md-block">
                    <label>Medium</label>
                    <Select
                      options={mediumOptions}
                      required
                      isSearchable={false}
                      value={
                        mediumOptions.find(
                          (opt) => opt.value === formData.medium_id,
                        ) || null
                      }
                      onChange={(e) => {
                        const value = e.value;
                        dispatch(setFormData({ name: "medium_id", value }));
                        dispatch(
                          GetClasssesNewThunk({
                            board_id: formData.board_id,
                            medium_id: value,
                          }),
                        );
                        // const fontSize = getFontSizeByMedium(mediumList, value);
                        // localStorage.setItem("font_size", fontSize);
                      }}
                      placeholder="Select Medium"
                      className="custom-select-wrapper custom-select create-paper-class"
                      classNamePrefix="custom-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          height: "38px",
                          minWidth: 0,
                          width: "100%",
                          flexWrap: "nowrap",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          height: "36px",
                          padding: "0 8px",
                          flexWrap: "nowrap",
                          overflow: "hidden",
                          minWidth: 0,
                          flex: 1,
                        }),
                        singleValue: (base) => ({
                          ...base,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          position: "absolute",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "calc(100% - 8px)",
                          color: "#808080",
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: "36px",
                          flexShrink: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: "none",
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: "4px",
                        }),
                      }}
                    />
                  </div>
                  {/* </div> */}
                  <div
                    className="w-50 position-relative class-step mobile-select-fix"
                    style={{ minWidth: 0, overflow: "hidden" }}
                  >
                    <label>Class</label>
                    <Select
                      // options={classes}
                      options={classesOptions}
                      required
                      isSearchable={false}
                      value={
                        classesOptions.find(
                          (opt) => opt.value === formData.class,
                        ) || null
                      }
                      onChange={async (e) => {
                        const name = "class";
                        const value = e.value;
                        handleOnChange({ target: { name, value } });
                        dispatch(GetSubjectsNewThunk({ class_id: value }));
                      }}
                      isDisabled={loader}
                      placeholder="Select Class"
                      className="custom-select-wrapper custom-select create-paper-class"
                      classNamePrefix="custom-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          height: "38px",
                          minWidth: 0,
                          width: "100%",
                          flexWrap: "nowrap",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          height: "36px",
                          padding: "0 8px",
                          flexWrap: "nowrap",
                          overflow: "hidden",
                          minWidth: 0,
                          flex: 1,
                        }),
                        singleValue: (base) => ({
                          ...base,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          position: "absolute",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "calc(100% - 8px)",
                          color: "#808080",
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: "36px",
                          flexShrink: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: "none",
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: "4px",
                        }),
                      }}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 school-crt-form mobile-class-subject-row">
                  <div
                    className="w-50 subject-step mobile-select-fix"
                    style={{ minWidth: 0, overflow: "hidden" }}
                  >
                    <label>Subject</label>
                    <Select
                      options={subjectOptions}
                      required
                      isSearchable={false}
                      value={
                        subjectOptions.find(
                          (opt) => opt.value === formData.subject,
                        ) || null
                      }
                      onChange={(e) => {
                        const name = "subject";
                        const value = e.value;
                        handleOnChange({ target: { name, value } });
                      }}
                      isDisabled={loader}
                      placeholder="Select Subject"
                      className="custom-select-wrapper custom-select create-paper-subject"
                      classNamePrefix="custom-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                        control: (base) => ({
                          ...base,
                          minHeight: "38px",
                          height: "38px",
                          minWidth: 0,
                          width: "100%",
                          flexWrap: "nowrap",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          height: "36px",
                          padding: "0 8px",
                          flexWrap: "nowrap",
                          overflow: "hidden",
                          minWidth: 0,
                          flex: 1,
                        }),
                        singleValue: (base) => ({
                          ...base,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          position: "absolute",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "calc(100% - 8px)",
                          color: "#808080",
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: "36px",
                          flexShrink: 0,
                        }),
                        indicatorSeparator: () => ({
                          display: "none",
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: "4px",
                        }),
                      }}
                    />
                  </div>
                  {/* Examination Name - Hidden on mobile, shown on desktop */}
                  <div className="w-50 exam-name-step d-none d-md-block">
                    <label>Examination Name</label>
                    <input
                      type="text"
                      className="custom-input"
                      placeholder="Enter Examination Name"
                      name="examName"
                      onChange={handleOnChange}
                      value={formData.examName}
                      // required
                    />
                  </div>
                </div>
                {/* Row 2: Maximum Marks + Duration - SIDE BY SIDE */}
                <div className="d-flex gap-2 mb-3 school-crt-form mobile-marks-duration-row">
                  <div className="w-50 maximun-marks-step">
                    <label>Maximum Marks</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="custom-input"
                      min={1}
                      name="maxMarks"
                      placeholder="Enter Marks"
                      value={formData.maxMarks}
                      onChange={handleOnChange}
                      required
                    />
                  </div>
                  <div className="w-50 duration-step">
                    <label>Duration </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "2%",
                        alignItems: "center",
                      }}
                    >
                      {/* HOURS INPUT */}
                      <input
                        type="number"
                        className="custom-input"
                        min={0}
                        max={5}
                        placeholder="HH"
                        value={formatTwoDigits(hours)}
                        onChange={(e) => {
                          let value = Number(e.target.value);

                          if (value < 0) return;
                          if (value > 5) value = 5;

                          dispatch(setField({ key: "hours", value }));
                          if ((value === 5) & (minutes > 0)) {
                            dispatch(setField({ key: "minutes", value: 0 }));
                          }
                        }}
                        required
                      />
                      Hrs
                      {/* MINUTES INPUT */}
                      <input
                        type="number"
                        className="custom-input"
                        min={0}
                        max={60}
                        placeholder="MM"
                        value={formatTwoDigits(minutes)}
                        onChange={(e) => {
                          let value = Number(e.target.value);

                          if (value < 0) return;

                          // 🚫 If max hours reached, block minutes > 0
                          if (hours === 5 && value > 0) {
                            alert("The maximum allowed duration is 5 hours.");
                            dispatch(setField({ key: "minutes", value: 0 }));
                            return;
                          }

                          // ⏱️ Convert 60 minutes → increment hour
                          if (value === 60) {
                            if (hours < 5) {
                              dispatch(
                                setField({ key: "hours", value: hours + 1 }),
                              );
                              dispatch(setField({ key: "minutes", value: 0 }));
                            }
                            return;
                          }

                          // ✅ Normal range
                          if (value <= 59) {
                            dispatch(setField({ key: "minutes", value }));
                          }
                        }}
                        required
                      />
                      Min
                    </div>
                  </div>
                </div>
                {/* {hours>0 &&!minutes ?`${hours} hrs`:} */}
                {/* Row 3: Examination Name - FULL WIDTH on mobile only */}
                <div className="mb-3 school-crt-form mobile-exam-name-row d-block d-md-none">
                  <div className="w-100 exam-name-step exam-name-step-mobile">
                    <label>Examination Name</label>
                    <input
                      type="text"
                      className="custom-input"
                      placeholder="Enter Examination Name"
                      name="examName"
                      onChange={handleOnChange}
                      value={formData.examName}
                      required
                    />
                  </div>
                </div>
                {/* Row 5: Terms and Conditions - Full width */}
                <div className="mb-1 terms-and-conditions-step">
                  <label style={{ marginBottom: "2px" }}>
                    Terms and Conditions
                  </label>
                  <textarea
                    className="custom-text-area"
                    name="termsText"
                    rows="4"
                    cols="150"
                    onChange={handleOnChange}
                    value={formData.termsText}
                    placeholder={`e.g.\n1. Any form of malpractice, cheating, or misbehavior during the exam will result in immediate disqualification.\n2. Use of electronic devices such as mobile phones, smartwatches, or calculators is strictly prohibited unless explicitly permitted.`}
                  ></textarea>
                </div>
                <button
                  className="btn-gradient mt-3 create-paper-select-qstns-btn select-questions-step"
                  type="submit"
                  disabled={loader}
                >
                  Move to Select Questions
                </button>
              </fieldset>
            </form>
          </div>
        </div>
      )}

      {stage === 2 && (
        <>
          <div className="d-flex gap-3 stage-2-panels">
            <div className="panel left-panel">
              <LeftPanel
                handleOnChange={handleOnChange}
                onStepChange={setCurrentStep}
                goToStepByTarget={goToStepByTarget}
              />
            </div>
            <div className="panel right-panel">
              <RightPanel />
            </div>
          </div>

          {/* Mobile Preview Button - Floating */}
          <button
            className="mobile-preview-btn"
            onClick={() => setShowPreviewModal(true)}
            aria-label="Preview Question Paper"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View your Question Paper
          </button>

          {/* Preview Modal */}
          <PreviewModal />
        </>
      )}

      {stage === 3 && (
        <Confirmation
          onStepChange={setCurrentStep}
          handleOnChange={handleOnChange}
          goToStepByTarget={goToStepByTarget}
        />
      )}

      {currentStep === 9 && displayMessage && (
        <div className="joy-inline-validation">
          {Array.isArray(validationMessage)
            ? validationMessage.join(", ")
            : validationMessage}
        </div>
      )}
    </>
  );
}

export default CreateQuestionPaper;
